import { useHotkeys } from "@mantine/hooks";
import { RefObject, useEffect } from "react";

import { TagOption } from "../constants/tagOptions";

type UsePitchTaggingHotkeysParams = {
  /** When false, no keys are bound (e.g. while the metrics view is shown) */
  enabled?: boolean;
  /** Options in the active section; only these hotkeys are live */
  options: TagOption[];
  isOptionDisabled?: (value: string) => boolean;
  onSelect: (value: string) => void;
  onNextSection: () => void;
  onPreviousSection: () => void;
  /** Bound to Enter; omit while saving isn't possible */
  onSave?: () => void;
  /** The tagging panel: Enter only saves when focus is in it (or nowhere) */
  panelRef: RefObject<HTMLElement | null>;
};

/**
 * Hotkeys for the Pitch Tagging panel. Letters repeat across sections
 * (B, S, L, F...), so only the active section's options are bound.
 * Tab / Shift+Tab move between sections; Enter saves, like the V1 tagger.
 */
export const usePitchTaggingHotkeys = ({
  enabled = true,
  options,
  isOptionDisabled,
  onSelect,
  onNextSection,
  onPreviousSection,
  onSave,
  panelRef,
}: UsePitchTaggingHotkeysParams) => {
  // Don't steal keys from an open dialog or menu (e.g. Edit Live Game State)
  const isDialogOpen = () =>
    !!document.querySelector('[role="dialog"], [role="menu"]');
  const whenNoDialog = (handler: () => void) => () => {
    if (!isDialogOpen()) handler();
  };

  // Not through useHotkeys, which always cancels the key: Enter on something
  // focused outside the panel (e.g. a sidebar pitch row) is that element's own
  useEffect(() => {
    if (!enabled || !onSave) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Enter" || isDialogOpen()) return;
      const focused = document.activeElement;
      if (
        focused &&
        focused !== document.body &&
        !panelRef.current?.contains(focused)
      ) {
        return;
      }
      // Also keeps a focused option button from toggling
      event.preventDefault();
      onSave();
    };
    document.documentElement.addEventListener("keydown", onKeyDown);
    return () =>
      document.documentElement.removeEventListener("keydown", onKeyDown);
  }, [enabled, onSave, panelRef]);

  useHotkeys(
    enabled
      ? [
          ["Tab", whenNoDialog(onNextSection)],
          ["shift+Tab", whenNoDialog(onPreviousSection)],
          ...options
            .filter(
              (option) => option.hotkey && !isOptionDisabled?.(option.value),
            )
            .map((option): [string, () => void] => [
              option.hotkey as string,
              whenNoDialog(() => onSelect(option.value)),
            ]),
        ]
      : [],
  );
};
